import type { PeriodPreset, PublicReportDataDto } from "@iagenix/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PeriodPicker, type PeriodValue } from "@/components/campaigns/period-picker";
import { FormField } from "@/components/form-field";
import { DashboardView } from "@/components/reports/dashboard-view";
import { ErrorState, LoadingScreen } from "@/components/states";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApiRequestError, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { reportsApi } from "@/lib/reports-api";
import { periodToParams } from "./app/$workspaceId/relatorios";

/** Página pública do relatório para o cliente final (link com token; senha opcional). */
export const Route = createFileRoute("/relatorio/$token")({
  component: RelatorioPublicoPage,
});

function RelatorioPublicoPage() {
  const { token } = Route.useParams();
  const info = useQuery(queries.publicReportInfo(token));
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState<string | null>(null);
  const [period, setPeriod] = useState<PeriodValue | null>(null);
  const [data, setData] = useState<PublicReportDataDto | null>(null);
  const load = useMutation({
    mutationFn: (input: { password?: string; period: PeriodValue }) =>
      reportsApi.unlockPublic(token, {
        ...(input.password ? { password: input.password } : {}),
        ...periodToParams(input.period),
      }),
    onSuccess: (d, vars) => {
      setData(d);
      setUnlocked(vars.password ?? "");
    },
  });
  const pdf = useMutation({
    mutationFn: () =>
      reportsApi.downloadPublicPdf(token, {
        ...(unlocked ? { password: unlocked } : {}),
        ...periodToParams(currentPeriod ?? { preset: "30d", range: { from: null, to: null } }),
      }),
    onError: (err) => toast.error(errorMessage(err)),
  });

  // Sem senha: carrega direto no período padrão do link (uma vez).
  const requiresPassword = info.data?.requiresPassword;
  const defaultPreset = info.data?.defaultPreset;
  const { mutate } = load;
  const started = useRef(false);
  useEffect(() => {
    if (requiresPassword === false && defaultPreset && !started.current) {
      started.current = true;
      mutate({
        period: { preset: defaultPreset as PeriodPreset, range: { from: null, to: null } },
      });
    }
  }, [requiresPassword, defaultPreset, mutate]);
  const currentPeriod: PeriodValue | null =
    period ??
    (defaultPreset
      ? { preset: defaultPreset as PeriodPreset, range: { from: null, to: null } }
      : null);

  if (info.isPending) return <LoadingScreen label="Abrindo o relatório…" />;
  if (info.isError) {
    const err = info.error;
    const gone = err instanceof ApiRequestError && (err.status === 404 || err.status === 410);
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <ErrorState
          error={err}
          title={gone ? "Este relatório não está disponível" : "Não foi possível abrir o relatório"}
          {...(gone ? {} : { onRetry: () => void info.refetch() })}
        />
      </div>
    );
  }
  const brand = info.data.brand;
  const accent = brand.brandColor;

  return (
    <div className="min-h-screen bg-muted/30">
      <header
        className="border-b bg-background"
        style={{ borderTopColor: accent, borderTopWidth: 4 }}
      >
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-3">
          {brand.logoUrl ? (
            <img
              src={brand.logoUrl}
              alt={brand.workspaceName}
              className="h-9 max-w-40 object-contain"
            />
          ) : (
            <span
              className="flex size-9 items-center justify-center rounded-md text-sm font-bold text-white"
              style={{ backgroundColor: accent }}
            >
              {brand.workspaceName.slice(0, 2).toUpperCase()}
            </span>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{brand.workspaceName}</p>
            <p className="truncate text-xs text-muted-foreground">
              Relatório de anúncios · {info.data.clientName}
            </p>
          </div>
          <ThemeToggle />
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-4 p-4 md:p-6">
        {unlocked === null ? (
          <form
            className="mx-auto max-w-sm space-y-4 rounded-xl border bg-card p-6 shadow-sm"
            onSubmit={(e) => {
              e.preventDefault();
              const initial: PeriodValue = {
                preset: (info.data.defaultPreset as PeriodPreset) || "30d",
                range: { from: null, to: null },
              };
              setPeriod(initial);
              load.mutate({ password, period: initial });
            }}
          >
            <div className="flex items-center gap-2">
              <Lock className="size-5 text-muted-foreground" aria-hidden />
              <h1 className="text-lg font-semibold">Relatório protegido</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              {requiresPassword
                ? "Informe a senha que a agência enviou para ver o relatório."
                : "Carregando…"}
            </p>
            {requiresPassword ? (
              <>
                <FormField
                  id="senha"
                  label="Senha"
                  error={load.isError ? errorMessage(load.error) : undefined}
                >
                  <Input
                    id="senha"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    autoComplete="current-password"
                  />
                </FormField>
                <Button
                  type="submit"
                  className="w-full"
                  loading={load.isPending}
                  style={{ backgroundColor: accent }}
                >
                  Abrir relatório
                </Button>
              </>
            ) : load.isError ? (
              <ErrorState error={load.error} />
            ) : null}
          </form>
        ) : (
          <>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="flex-1">
                <h1 className="text-xl font-semibold">{info.data.clientName}</h1>
                {data ? (
                  <p className="text-xs text-muted-foreground">
                    {data.dashboard.period.from.split("-").reverse().join("/")} a{" "}
                    {data.dashboard.period.to.split("-").reverse().join("/")} · comparado com o
                    período anterior
                    {info.data.expiresAt
                      ? ` · link válido até ${new Date(info.data.expiresAt).toLocaleDateString("pt-BR")}`
                      : ""}
                  </p>
                ) : null}
              </div>
              {currentPeriod ? (
                <PeriodPicker
                  value={currentPeriod}
                  onChange={(v) => {
                    const next =
                      v.preset === "maximo"
                        ? { preset: "30d" as const, range: { from: null, to: null } }
                        : v;
                    setPeriod(next);
                    load.mutate({ ...(unlocked ? { password: unlocked } : {}), period: next });
                  }}
                />
              ) : null}
              <Button variant="outline" onClick={() => pdf.mutate()} loading={pdf.isPending}>
                <Download />
                Baixar PDF
              </Button>
            </div>
            {load.isPending && !data ? (
              <LoadingScreen />
            ) : load.isError ? (
              <ErrorState
                error={load.error}
                onRetry={() =>
                  currentPeriod
                    ? load.mutate({
                        ...(unlocked ? { password: unlocked } : {}),
                        period: currentPeriod,
                      })
                    : undefined
                }
              />
            ) : data ? (
              <DashboardView dashboard={data.dashboard} accent={accent} />
            ) : null}
          </>
        )}
        <p className="pt-6 text-center text-xs text-muted-foreground">
          Relatório gerado por {brand.workspaceName} com IAGENIX Ads.
        </p>
      </main>
    </div>
  );
}
